import {
  formatHeaderText,
  operatorRequiresValue,
  ensureFilterIds,
  serializeExportDataFilters,
} from '../shared/tmResultsDataTableUtils.js';

/**
 * Normalise API sort items to internal { key, direction } shape.
 * @param {Array} sort
 * @returns {Array<{key: string, direction: string}>}
 */
export function normalizeSortItems(sort) {
  if (!Array.isArray(sort)) return [];
  return sort
    .map((item) => ({
      key: item?.key || item?.field || '',
      direction: item?.direction === 'desc' ? 'desc' : 'asc',
    }))
    .filter((item) => item.key);
}

/**
 * Build TMResultsTableWrapper header objects from API header keys.
 * @param {string[]} apiHeaders
 * @param {Object} columnTypeMap
 * @returns {Array<{key: string, label: string, isNumeric: boolean}>}
 */
export function buildTableHeadersFromApi(apiHeaders = [], columnTypeMap = {}) {
  return (apiHeaders || [])
    .filter((headerKey) => headerKey && !String(headerKey).startsWith('_'))
    .map((headerKey) => {
      const columnType = columnTypeMap[headerKey] || 'string';
      return {
        key: headerKey,
        label: formatHeaderText(headerKey),
        isNumeric: columnType === 'number',
      };
    });
}

/**
 * Build filter column dropdown items from API headers.
 * @param {string[]} apiHeaders
 * @returns {Array<{label: string, value: string}>}
 */
export function buildFilterColumnsFromApi(apiHeaders = []) {
  return (apiHeaders || [])
    .filter((header) => header && !String(header).startsWith('_'))
    .map((header) => ({
      label: formatHeaderText(header),
      value: header,
    }));
}

/**
 * Count valid filter conditions for toolbar badge display.
 * @param {Object} filters
 * @returns {number}
 */
export function countActiveFilterConditions(filters) {
  const isValidCondition = (condition) => {
    if (!condition || !condition.column) return false;
    if (!operatorRequiresValue(condition.operator)) return true;
    if (condition.value === null || condition.value === undefined) return false;
    if (typeof condition.value === 'number') return !Number.isNaN(condition.value);
    return String(condition.value).trim() !== '';
  };

  if (!filters?.groups?.length) return 0;
  return filters.groups.reduce((count, group) => {
    const valid = (group.conditions || []).filter(isValidCondition);
    return count + valid.length;
  }, 0);
}

/**
 * Prepare default/saved filters from API for the filter builder.
 * @param {Object|null} defaultFilters
 * @returns {Object}
 */
export function normalizeDefaultFilters(defaultFilters) {
  const withIds = ensureFilterIds(defaultFilters);
  if (withIds?.groups?.length) return withIds;
  return { groups: [], group_connectors: [] };
}

/**
 * Map drill-down bucket entries to table wrapper props.
 * @param {Object} bucket
 * @param {Object} columnTypeMap
 * @returns {{ name: string, records: Array, tableHeaders: Array, totalCount: number }}
 */
export function mapBucketForDisplay(bucket = {}, columnTypeMap = {}) {
  const headers = bucket.headers || [];
  return {
    name: bucket.name || bucket.label || 'Bucket',
    records: bucket.records || [],
    tableHeaders: buildTableHeadersFromApi(headers, columnTypeMap),
    totalCount: bucket.totalCount || (bucket.records || []).length,
  };
}

/**
 * Serialize drill-down export filters for Excel download `dataFilters`.
 * @param {Array|string|null|undefined} filters
 * @returns {string|null}
 */
export function buildDrilldownDataFilters(filters) {
  return serializeExportDataFilters(filters);
}

/**
 * Resolve summary amount from a report row — prefer last numeric column (Final Amount),
 * fall back to first numeric column (Sub Amount).
 * @param {Object} row
 * @param {string[]} headers
 * @returns {number|null}
 */
export function getRowSummaryAmount(row, headers = []) {
  if (!row || !Array.isArray(headers) || headers.length < 2) return null;

  const numericHeaders = headers.slice(1);
  const lastKey = numericHeaders[numericHeaders.length - 1];
  const lastVal = row[lastKey];
  if (lastVal !== null && lastVal !== undefined && !Number.isNaN(Number(lastVal))) {
    return Number(lastVal);
  }

  if (numericHeaders.length > 1) {
    const subVal = row[numericHeaders[0]];
    if (subVal !== null && subVal !== undefined && !Number.isNaN(Number(subVal))) {
      return Number(subVal);
    }
  }

  return null;
}

/**
 * Serialise collapsed section Sets for Vuex snapshot storage.
 * @param {Object} collapsedSections
 * @returns {Object}
 */
export function serializeCollapsedSections(collapsedSections = {}) {
  const result = {};
  Object.entries(collapsedSections).forEach(([tableName, sections]) => {
    result[tableName] = sections instanceof Set ? Array.from(sections) : (sections || []);
  });
  return result;
}

/**
 * Restore collapsed section Sets from a Vuex snapshot.
 * @param {Object} serialized
 * @returns {Object}
 */
export function deserializeCollapsedSections(serialized = {}) {
  const result = {};
  Object.entries(serialized).forEach(([tableName, sections]) => {
    result[tableName] = new Set(Array.isArray(sections) ? sections : []);
  });
  return result;
}
